/**
 * Shipping Timeline - Date Calculation Engine
 *
 * Calculates estimated delivery dates based on:
 * - Processing time (working days)
 * - Transit time (working days)
 * - Cutoff time (orders after this time process next working day)
 * - Blocked dates (holidays)
 *
 * Uses customer's local timezone for all calculations.
 *
 * BEGIN CUSTOM EDIT: Shipping Timeline | Date: 2025-11-27
 * What: Native shipping timeline estimator replacing external app
 */

if (!customElements.get('shipping-timeline')) {
  class ShippingTimeline extends HTMLElement {
    constructor() {
      super();
      this.config = null;
    }

    connectedCallback() {
      this.config = this.parseConfig();
      this.calculateAndRender();
    }

    parseConfig() {
      return {
        processingMin: parseInt(this.dataset.processingMin, 10) || 0,
        processingMax: parseInt(this.dataset.processingMax, 10) || 1,
        processingCutoff: parseInt(this.dataset.processingCutoff, 10) || 15,
        processingDays: this.parseJsonArray(this.dataset.processingDays, [1, 2, 3, 4, 5, 6]),
        transitMin: parseInt(this.dataset.transitMin, 10) || 1,
        transitMax: parseInt(this.dataset.transitMax, 10) || 5,
        transitDays: this.parseJsonArray(this.dataset.transitDays, [1, 2, 3, 4, 5, 6]),
        blockedDates: this.parseBlockedDates(this.dataset.blockedDates || ''),
        locale: this.dataset.locale || 'en'
      };
    }

    parseJsonArray(str, fallback) {
      try {
        const parsed = JSON.parse(str);
        return Array.isArray(parsed) ? parsed : fallback;
      } catch {
        return fallback;
      }
    }

    parseBlockedDates(str) {
      if (!str) return [];
      return str
        .split(',')
        .map(d => d.trim())
        .filter(d => d.length > 0 && d !== 'undefined');
    }

    calculateAndRender() {
      const dates = this.calculateDates();
      this.render(dates);
    }

    calculateDates() {
      const now = new Date();
      const currentHour = now.getHours();

      // Start with today as purchase date
      let purchaseDate = new Date(now);

      // Determine if we need to push processing to next working day
      // If after cutoff OR today is not a processing day
      let processingStartDate = new Date(purchaseDate);

      if (currentHour >= this.config.processingCutoff || !this.isWorkingDay(purchaseDate, this.config.processingDays)) {
        processingStartDate = this.getNextWorkingDay(purchaseDate, this.config.processingDays);
      }

      // Calculate processing end dates (min and max)
      const processingEndMin = this.addWorkingDays(
        processingStartDate,
        this.config.processingMin,
        this.config.processingDays
      );
      const processingEndMax = this.addWorkingDays(
        processingStartDate,
        this.config.processingMax,
        this.config.processingDays
      );

      // Calculate delivery dates (min and max) starting from processing end
      const deliveryMin = this.addWorkingDays(
        processingEndMin,
        this.config.transitMin,
        this.config.transitDays
      );
      const deliveryMax = this.addWorkingDays(
        processingEndMax,
        this.config.transitMax,
        this.config.transitDays
      );

      return {
        purchased: purchaseDate,
        processingMin: processingEndMin,
        processingMax: processingEndMax,
        deliveryMin: deliveryMin,
        deliveryMax: deliveryMax
      };
    }

    isBlockedDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      // Check for yearly format (MM-DD) and specific format (YYYY-MM-DD)
      const yearlyFormat = `${month}-${day}`;
      const specificFormat = `${year}-${month}-${day}`;

      return this.config.blockedDates.some(blocked => {
        const trimmed = blocked.trim();
        return trimmed === yearlyFormat || trimmed === specificFormat;
      });
    }

    isWorkingDay(date, workingDays) {
      const dayOfWeek = date.getDay();
      return workingDays.includes(dayOfWeek) && !this.isBlockedDate(date);
    }

    getNextWorkingDay(date, workingDays) {
      const result = new Date(date);
      result.setDate(result.getDate() + 1);

      // Safety limit to prevent infinite loops
      let iterations = 0;
      const maxIterations = 365;

      while (!this.isWorkingDay(result, workingDays) && iterations < maxIterations) {
        result.setDate(result.getDate() + 1);
        iterations++;
      }

      return result;
    }

    addWorkingDays(startDate, days, workingDays) {
      if (days === 0) return new Date(startDate);

      let result = new Date(startDate);
      let addedDays = 0;

      // Safety limit to prevent infinite loops
      let iterations = 0;
      const maxIterations = 365;

      while (addedDays < days && iterations < maxIterations) {
        result.setDate(result.getDate() + 1);
        if (this.isWorkingDay(result, workingDays)) {
          addedDays++;
        }
        iterations++;
      }

      return result;
    }

    formatDate(date) {
      // Use Intl.DateTimeFormat for localized date formatting
      const options = { month: 'short', day: 'numeric' };
      try {
        return date.toLocaleDateString(this.config.locale, options);
      } catch {
        // Fallback to English if locale is not supported
        return date.toLocaleDateString('en', options);
      }
    }

    formatDateRange(minDate, maxDate) {
      // Same date - just show the single localized date
      if (minDate.toDateString() === maxDate.toDateString()) {
        return this.formatDate(minDate);
      }

      // BEGIN CUSTOM EDIT: Localized date range | Date: 2026-06-02 | What: Intl.formatRange follows locale word order (de: "5.-10. Juni"); was hardcoded English "Mon D - D"
      const options = { month: 'short', day: 'numeric' };
      try {
        return new Intl.DateTimeFormat(this.config.locale, options).formatRange(minDate, maxDate);
      } catch (e) {
        try {
          return new Intl.DateTimeFormat('en', options).formatRange(minDate, maxDate);
        } catch (e2) {
          return `${this.formatDate(minDate)} - ${this.formatDate(maxDate)}`;
        }
      }
      // END CUSTOM EDIT
    }

    render(dates) {
      const purchasedEl = this.querySelector('[data-purchased-date]');
      const processingEl = this.querySelector('[data-processing-date]');
      const deliveryEl = this.querySelector('[data-delivery-date]');

      if (purchasedEl) {
        purchasedEl.textContent = this.formatDate(dates.purchased);
      }

      if (processingEl) {
        processingEl.textContent = this.formatDateRange(dates.processingMin, dates.processingMax);
      }

      if (deliveryEl) {
        deliveryEl.textContent = this.formatDateRange(dates.deliveryMin, dates.deliveryMax);
      }
    }
  }

  customElements.define('shipping-timeline', ShippingTimeline);
}

/* END CUSTOM EDIT */
